import { PlayerSnapshot, GameMode, MatchState, GameEvent } from '../shared/types';
import { MAX_PLAYERS, TICK_RATE, FIXED_DT, SNAPSHOT_SEND_RATE } from '../shared/constants';
import { generateId } from '../shared/utils';
import { PhysicsEngine, PlayerPhysicsState } from '../engine/Physics';
import { PlayerInput } from '../shared/types';

export interface ServerPlayer {
  id: string;
  name: string;
  team: string;
  state: PlayerPhysicsState;
  inputBuffer: PlayerInput[];
  lastProcessedInput: number;
  kills: number;
  deaths: number;
  assists: number;
  connected: boolean;
  lastActivity: number;
}

export interface MatchConfig {
  gameMode: GameMode;
  mapId: string;
  maxPlayers: number;
  timeLimit: number;
  scoreLimit: number;
  friendlyFire: boolean;
  respawnTime: number;
  customRules: Record<string, unknown>;
}

export interface ServerState {
  matchState: MatchState;
  timeRemaining: number;
  scores: Record<string, number>;
  events: GameEvent[];
}

export class GameServer {
  public players = new Map<string, ServerPlayer>();
  public physics: PhysicsEngine;
  public matchConfig: MatchConfig;
  public state: ServerState;
  public tickRate = TICK_RATE;
  public tickCount = 0;
  private snapshotTimer = 0;

  constructor(config: Partial<MatchConfig> = {}) {
    this.physics = new PhysicsEngine();
    this.matchConfig = {
      gameMode: config.gameMode || 'ffa',
      mapId: config.mapId || 'arena',
      maxPlayers: config.maxPlayers || MAX_PLAYERS,
      timeLimit: config.timeLimit || 600,
      scoreLimit: config.scoreLimit || 30,
      friendlyFire: config.friendlyFire ?? false,
      respawnTime: config.respawnTime || 3,
      customRules: config.customRules || {},
    };
    this.state = {
      matchState: 'waiting',
      timeRemaining: this.matchConfig.timeLimit,
      scores: {},
      events: [],
    };
  }

  addPlayer(id: string, name: string, team = ''): ServerPlayer {
    if (this.players.size >= this.matchConfig.maxPlayers) {
      throw new Error('Server full');
    }
    const state = this.physics.createPlayerState(
      (Math.random() - 0.5) * 40,
      0,
      (Math.random() - 0.5) * 40
    );
    const player: ServerPlayer = {
      id,
      name,
      team,
      state,
      inputBuffer: [],
      lastProcessedInput: 0,
      kills: 0,
      deaths: 0,
      assists: 0,
      connected: true,
      lastActivity: Date.now(),
    };
    this.players.set(id, player);
    this.state.scores[id] = 0;
    this.addEvent('join', `${name} joined the game`, id);
    return player;
  }

  removePlayer(id: string): void {
    const player = this.players.get(id);
    if (player) {
      player.connected = false;
      this.addEvent('leave', `${player.name} left the game`, id);
      setTimeout(() => {
        if (!player.connected) {
          this.players.delete(id);
        }
      }, 5000);
    }
  }

  processInput(playerId: string, input: PlayerInput): void {
    const player = this.players.get(playerId);
    if (!player || !player.connected) return;
    player.lastActivity = Date.now();
    if (player.inputBuffer.length < 10) {
      player.inputBuffer.push(input);
    }
  }

  tick(dt: number): void {
    this.tickCount++;

    if (this.state.matchState === 'playing') {
      this.state.timeRemaining -= dt;
      if (this.state.timeRemaining <= 0) {
        this.endMatch();
        return;
      }
    }

    for (const player of this.players.values()) {
      if (!player.connected) continue;
      const input = player.inputBuffer.shift();
      if (input) {
        player.lastProcessedInput = input.seq;
        this.physics.simulatePlayer(player.state, input, dt, performance.now() / 1000);
      } else {
        const idleInput: PlayerInput = {
          seq: 0,
          forward: false, back: false, left: false, right: false,
          jump: false, crouch: false, sprint: false, slide: false,
          fire: false, aim: false, reload: false,
          weaponSlot: 'primary', yaw: player.state.yaw, pitch: player.state.pitch,
          mouseDeltaX: 0, mouseDeltaY: 0, timestamp: performance.now(),
        };
        this.physics.simulatePlayer(player.state, idleInput, dt, performance.now() / 1000);
      }
    }

    this.checkKills();

    this.snapshotTimer += dt;
  }

  shouldSendSnapshot(): boolean {
    if (this.snapshotTimer >= 1 / SNAPSHOT_SEND_RATE) {
      this.snapshotTimer = 0;
      return true;
    }
    return false;
  }

  getSnapshot(): PlayerSnapshot[] {
    const snapshots: PlayerSnapshot[] = [];
    for (const player of this.players.values()) {
      snapshots.push({
        id: player.id,
        name: player.name,
        x: player.state.position.x,
        y: player.state.position.y,
        z: player.state.position.z,
        vx: player.state.velocity.x,
        vy: player.state.velocity.y,
        vz: player.state.velocity.z,
        yaw: player.state.yaw,
        pitch: player.state.pitch,
        health: Math.round(player.state.health),
        armor: Math.round(player.state.armor),
        alive: player.state.alive,
        crouching: player.state.crouching,
        sprinting: player.state.sprinting,
        sliding: player.state.sliding,
        onGround: player.state.onGround,
        currentWeapon: 'primary',
        ammo: 30,
        kills: player.kills,
        deaths: player.deaths,
        assists: player.assists,
        team: player.team,
      });
    }
    return snapshots;
  }

  private checkKills(): void {
    for (const player of this.players.values()) {
      if (!player.state.alive) {
        this.respawnPlayer(player);
      }
    }
  }

  private respawnPlayer(player: ServerPlayer): void {
    const x = (Math.random() - 0.5) * 30;
    const z = (Math.random() - 0.5) * 30;
    this.physics.respawn(player.state, x, 0, z);
  }

  private endMatch(): void {
    this.state.matchState = 'ended';
    this.addEvent('objective', 'Match ended!');
  }

  startMatch(): void {
    this.state.matchState = 'playing';
    this.state.timeRemaining = this.matchConfig.timeLimit;
    this.addEvent('objective', 'Match started!');
  }

  private addEvent(type: GameEvent['type'], text: string, playerId = ''): void {
    const event: GameEvent = {
      id: generateId(),
      type,
      text,
      timestamp: Date.now(),
      playerId,
    };
    this.state.events.push(event);
    if (this.state.events.length > 100) {
      this.state.events.shift();
    }
  }

  getServerState(): ServerState {
    return { ...this.state };
  }
}
